import { renderRichText } from 'gatsby-source-contentful/rich-text';
import {
  ContentProps,
  ContentRawParsed,
  ContentReference,
  processRaw,
  processReferences,
} from './helpers';

const Content = ({ content, options, nested = false }: ContentProps) => {
  const sortRows = (
    raw: {
      content: ContentGrid['nodes'];
    },
    references: ContentReference[],
  ) => {
    const rows: ContentGrid[] = [];
    let arrIndex = 0;
    let lastResolution = '';

    raw.content.forEach((node, i: number) => {
      const wasEmbeddedBlock =
        raw.content[i - 1]?.nodeType === 'embedded-entry-block';
      const isEmbeddedBlock = node.nodeType === 'embedded-entry-block';
      const isHeading2 = node.nodeType === 'heading-2';

      if (
        rows[arrIndex] &&
        (wasEmbeddedBlock || isEmbeddedBlock || isHeading2)
      ) {
        arrIndex++;
      }

      rows[arrIndex] = rows[arrIndex] || {
        type: isEmbeddedBlock ? 'embedded' : 'texted',
        nodes: [],
        multiItem: false,
        firstRow: false,
        lastRow: false,
        crossRow: false,
      };

      if (node.data?.target) {
        const reference = references.find(
          (ref) => ref.contentful_id === node.data?.target?.sys.id,
        );

        if (reference) {
          node.resolution = reference.__typename;
          // merge publication component cells
          if (node.resolution === 'ContentfulPublication') {
            if (node.resolution === lastResolution) {
              rows.splice(arrIndex, 1);
              arrIndex--;
            } else {
              rows[arrIndex].multiItem = true;
            }
          }

          // remove spacing before and after anchor tag
          if (node.resolution === 'ContentfulAnchorTag') {
            rows[arrIndex].lastRow = true;
          }

          // reduce spacing between event hub elements
          if (
            node.resolution === 'ContentfulEventHub' &&
            node.resolution === lastResolution
          ) {
            rows[arrIndex - 1].crossRow = true;
          }

          // remove spacing between collapsible content elements
          if (
            node.resolution === 'ContentfulElementCollapsibleContent' &&
            node.resolution === lastResolution
          ) {
            rows[arrIndex - 1].lastRow = true;
          }

          lastResolution = node.resolution;
        }
      } else {
        lastResolution = node.nodeType;
      }

      if (i === 0) {
        rows[arrIndex].firstRow = true;
      }

      if (i === raw.content.length - 1) {
        rows[arrIndex].lastRow = true;
      }

      rows[arrIndex].nodes.push(node);
    });

    rows.forEach((row, i) => {
      // Assign first/last node
      row.nodes[0].firstNode = true;
      row.nodes[row.nodes.length - 1].lastNode = true;

      // Reduce spacing between texted and embedded rows
      if (rows[i - 1]?.type === 'texted' && row.type === 'embedded') {
        const nodeResolution = row.nodes[0].resolution ?? '';

        switch (nodeResolution) {
          case 'ContentfulConsultationTeaserModule':
          case 'ContentfulFundingTeaserModule':
          case 'ContentfulStoryTeaserModule':
          case 'ContentfulToolTeaserModule':
          case 'ContentfulCallToActionModule':
          case 'ContentfulFaqModule':
          case 'ContentfulFactsModule':
          case 'ContentfulQuotation':
          case 'ContentfulEventTeaserModule':
            // These modules always have an H2 heading - leave spacing as is
            break;
          case 'ContentfulGenericTeaserModule': {
            const reference = references.find(
              (node) =>
                node.contentful_id === row.nodes[0].data?.target?.sys.id,
            );

            if (!reference?.title) {
              // Reduce spacing only if module has no title
              rows[i - 1].crossRow = true;
            }

            break;
          }
          default:
            // Reduce spacing for any other cases
            rows[i - 1].crossRow = true;
        }
      }

      // Reduce spacing between embedded and texted rows
      if (rows[i - 1]?.type === 'embedded' && row.type === 'texted') {
        const textType = row.nodes[0].nodeType;
        const allowedTextTypes = [
          'paragraph',
          'heading-4',
          'heading-5',
          'heading-6',
        ];
        const embedResolution = rows[i - 1].nodes[0].resolution ?? '';
        const disallowedEmbedResolutions = ['ContentfulQuotation'];

        if (
          allowedTextTypes.includes(textType) &&
          !disallowedEmbedResolutions.includes(embedResolution)
        ) {
          rows[i - 1].crossRow = true;
        }
      }
    });

    return rows;
  };

  const wrapGrid = (rows: ContentGrid[]) => {
    const excludedTypes = [
      'ContentfulYoutubeLink',
      'ContentfulPodcastHub',
      'ContentfulEventHub',
    ];

    return rows.map((row) => {
      const fullWidth =
        nested ||
        (row.type === 'embedded' &&
          !excludedTypes.includes(row.nodes[0].resolution ?? ''));

      return {
        data: {
          target: {
            type: row.type,
            grid: {
              size: 12,
              col: fullWidth ? 12 : { zero: 12, medium: 10, large: 8 },
              push: fullWidth ? 0 : { zero: 0, medium: 1, large: 2 },
            },
          },
        },
        content: row.nodes,
        multiItem: row.multiItem,
        firstRow: row.firstRow,
        lastRow: row.lastRow,
        crossRow: row.crossRow,
        nodeType: 'grid',
        nested,
      };
    });
  };

  const raw = processRaw(content.raw);
  const references = processReferences(content.references ?? []);

  return renderRichText(
    {
      ...content,
      raw: JSON.stringify({
        ...raw,
        content: wrapGrid(sortRows(raw, references)),
      }),
    },
    options,
  );
};

type ContentGrid = {
  type: 'embedded' | 'texted';
  nodes: ContentRawParsed['content'];
  multiItem: boolean;
  firstRow: boolean;
  lastRow: boolean;
  crossRow: boolean;
};

export default Content;
